import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from "@angular/core";
import { IToggleComponent, ToasterService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { SegmentConfigType, SegmentEx, SpStrategyEx, StrategyEx } from "@front/m19-models";
import { TacticType } from "@front/m19-api-client";
import { StrategyService } from "@front/m19-injection-token";

import { SettingRowComponent } from "../setting-row/setting-row.component";
@Component({
  selector: "app-toggle-product-targeting",
  imports: [IToggleComponent, TranslocoDirective, SettingRowComponent],
  template: `@if (spStrategy(); as strategy) {
    <app-setting-row *transloco="let t">
      <ng-container label>
        {{ t("advanced-settings-modal.product_targeting") }}
      </ng-container>
      <IToggle
        [disabled]="isReadOnly() || isProductTargetingDisabled()"
        testid="product-targeting"
        [checked]="strategy.productTargetingEnabled"
        (checkedChange)="switchProductTargeting()"
        [loading]="isUpdating()"
      />
    </app-setting-row>
  }`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToggleProductTargetingComponent {
  private readonly strategyService = inject(StrategyService);
  private readonly toasterService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);

  strategy = input.required<StrategyEx>();
  organizationId = input.required<number>();
  isReadOnly = input.required<boolean>();
  segmentIndex = input.required<Map<number, SegmentEx>>();

  spStrategy = computed(() => {
    const strategy = this.strategy();
    return strategy.isSpStrategy() ? strategy : undefined;
  });

  isProductTargetingDisabled = computed(() => {
    const strategy = this.spStrategy();
    if (!strategy) return true;

    return (
      !strategy.autoAlgoExplorationEnabled &&
      strategy.tactics.length > 0 &&
      strategy.tactics.every((t) => {
        const segment = this.segmentIndex()?.get(t.segmentId);
        return segment?.segmentType == SegmentConfigType.ProductSegment || t.tacticType == TacticType.BLACKLIST;
      })
    );
  });

  isUpdating = signal(false);

  switchProductTargeting() {
    const spStrategy = this.spStrategy();
    if (!spStrategy) return;
    this.isUpdating.set(true);
    this.strategyService
      .updateStrategyProductTargeting(
        spStrategy.accountId,
        spStrategy.marketplace,
        this.organizationId()!,
        spStrategy.strategyId,
        !spStrategy.productTargetingEnabled,
      )
      .subscribe({
        next: (s) => {
          const updatedStrategy = s as SpStrategyEx;
          this.toasterService.success(
            updatedStrategy.productTargetingEnabled
              ? this.translocoService.translate("strategy-page.product_targeting_enabled")
              : this.translocoService.translate("strategy-page.product_targeting_disabled"),
            this.translocoService.translate("advanced-settings-modal.product_targeting"),
          );
          this.isUpdating.set(false);
        },
        error: (error) => {
          this.toasterService.error(error, this.translocoService.translate("strategy-page.product_targeting_error"));
          this.isUpdating.set(false);
        },
      });
  }
}
